import { ArrowRight, Check, Instagram, MapPin, Plus } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { PropertyCard } from '@/components/property-card';
import { NoFilterMatch, PropertyFilterBar, usePropertyFilters } from '@/components/property-filters';
import { formatPhoneDisplay } from '@/lib/phone-format';
import type { LandingProps } from '../types';
import {
  CRECI,
  FACE,
  INCOME_OPTIONS,
  LAUNCH_NOTE,
  OBJECTIONS,
  PORTRAIT,
  STEPS,
  useAngelPage,
  useScrolledPast,
  useStartWithIncome,
  type AngelPage,
} from './shared';

// =============================================================================
// Angel · "Padrão 2". O padrão do mercado levado ao limite do que converte:
// a primeira pergunta do formulário (renda da família) já está no topo, e
// tocar numa faixa começa a análise com ela marcada. Mostra as faixas oficiais
// do MCMV 2026 com o Floratta posicionado, a vizinhança real do prédio e a
// Angel como pessoa, perto de cada ação.
// =============================================================================

const P = {
  navy: '#1E2A5A',
  ink: '#18224A',
  muted: '#4A5275',
  sand: '#F4EFE8',
  line: '#E4DCCF',
  gold: '#C9A36A',
  goldSoft: '#F3E7D3',
  goldInk: '#7A5A22',
} as const;

const SERIF = '"Libre Caslon Text", Georgia, serif';
const SANS = '"Archivo", system-ui, sans-serif';

// Portaria MCID nº 333, de 30/03/2026 (limites urbanos de renda bruta familiar).
const FAIXAS: Array<{ name: string; range: string; max: number }> = [
  { name: 'Faixa 1', range: 'até R$ 3.200', max: 3200 },
  { name: 'Faixa 2', range: 'R$ 3.200,01 a R$ 5.000', max: 5000 },
  { name: 'Faixa 3', range: 'R$ 5.000,01 a R$ 9.600', max: 9600 },
  { name: 'Faixa 4', range: 'R$ 9.600,01 a R$ 13.000', max: 13000 },
];

/** "Renda a partir de R$ 5.500" → 5500. */
function incomeFloor(income: string | null): number | null {
  const m = income?.match(/R\$\s*([\d.]+)/);
  return m?.[1] ? Number(m[1].replace(/\./g, '')) : null;
}

export function Padrao2Landing(props: LandingProps) {
  const page = useAngelPage(props);
  const { broker, onCta } = props;
  const hero = useScrolledPast<HTMLElement>();
  const start = useStartWithIncome(page, onCta);

  return (
    <div className="p2 min-h-screen bg-white" style={{ color: P.ink, fontFamily: SANS }}>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800&family=Libre+Caslon+Text:ital@1&display=swap"
        />
        <meta name="theme-color" content={P.navy} />
      </Helmet>
      <style>{`
        .p2 ::selection { background: ${P.gold}; color: ${P.ink}; }
        .p2 :focus-visible { outline: 3px solid ${P.navy}; outline-offset: 3px; }
        .p2 [data-dark] :focus-visible { outline-color: ${P.gold}; }
        html { scrollbar-color: ${P.navy} ${P.sand}; }
      `}</style>

      <header data-dark className="sticky top-0 z-30" style={{ backgroundColor: P.navy }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <img src={FACE} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white/25" />
            <div className="min-w-0 leading-tight text-white">
              <div className="truncate text-lg italic" style={{ fontFamily: SERIF }}>
                {broker.name}
              </div>
              <div className="text-xs text-white/80">
                Corretora MCMV<span className="hidden sm:inline"> · {CRECI}</span>
              </div>
            </div>
          </div>
          <Action page={page} onCta={onCta} label="Começar" small />
        </div>
      </header>

      <main>
        <Hero page={page} broker={broker} start={start} heroRef={hero.ref} />
        <FactsStrip page={page} />
        <Faixas page={page} start={start} />
        <Steps />
        <Objections />
        {page.lead && <Building page={page} onCta={onCta} />}
        <About broker={broker} page={page} />
        {page.others.length > 0 && <Others page={page} />}
        <Faq items={props.content.faqItems} />
        <Final start={start} />
      </main>

      <Footer broker={broker} />

      <div
        className={`fixed inset-x-0 bottom-0 z-30 border-t bg-white px-4 py-3 shadow-[0_-8px_20px_-12px_rgba(24,34,74,.3)] transition-transform duration-300 md:hidden ${hero.past ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ borderColor: P.line }}
        aria-hidden={!hero.past}
      >
        <Action page={page} onCta={onCta} full tabIndex={hero.past ? 0 : -1} />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Peças
// ----------------------------------------------------------------------------

function Action({
  page,
  onCta,
  label = 'Começar minha análise grátis',
  small = false,
  full = false,
  tabIndex,
}: {
  page: AngelPage;
  onCta: () => void;
  label?: string;
  small?: boolean;
  full?: boolean;
  tabIndex?: number;
}) {
  const className = `group inline-flex items-center justify-center gap-2 rounded-xl font-bold transition-[filter] hover:brightness-105 ${
    small ? 'min-h-11 shrink-0 px-4 text-sm' : 'min-h-14 px-7 text-lg'
  } ${full ? 'w-full' : ''}`;
  const style: CSSProperties = { backgroundColor: P.gold, color: P.ink };
  const content = (
    <>
      {label}
      {!small && <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />}
    </>
  );
  return page.startHref ? (
    <Link to={page.startHref} className={className} style={style} tabIndex={tabIndex}>
      {content}
    </Link>
  ) : (
    <button type="button" onClick={onCta} className={className} style={style} tabIndex={tabIndex}>
      {content}
    </button>
  );
}

/**
 * A primeira pergunta do formulário, funcionando aqui: cada faixa é um botão
 * que começa a análise com a renda já marcada.
 */
// Rótulo enxuto pra caber em uma linha no botão (a dica diz que é em R$).
const LABELS: Record<number, string> = {
  400_000: 'Até 5 mil',
  730_000: '5 a 9,6 mil',
  1_130_000: '9,6 a 13 mil',
  1_500_000: 'Mais de 13 mil',
};

function IncomeQuestion({ start, tone = 'light', id }: { start: (cents: number) => void; tone?: 'light' | 'dark'; id: string }) {
  const dark = tone === 'dark';
  return (
    <fieldset aria-describedby={`${id}-hint`}>
      <legend className={`text-balance text-xl font-bold leading-snug ${dark ? 'text-white' : ''}`}>
        Pra começar: qual a renda da sua família por mês?
      </legend>
      <p id={`${id}-hint`} className={`mt-1 text-sm ${dark ? 'text-white/80' : ''}`} style={dark ? undefined : { color: P.muted }}>
        Some a renda de quem vai morar com você, em R$.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-2.5">
        {INCOME_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => start(o.value)}
            className="group flex min-h-14 items-center justify-between gap-2 rounded-xl border-2 px-4 py-3 text-left text-base font-semibold transition-colors"
            style={
              dark
                ? { borderColor: 'rgba(255,255,255,.6)', color: '#FFFFFF' }
                : { borderColor: P.gold, color: P.ink, backgroundColor: P.goldSoft }
            }
          >
            <span className="whitespace-nowrap">{LABELS[o.value] ?? o.label}</span>
            <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function Section({ children, tone = 'white', className = '' }: { children: ReactNode; tone?: 'white' | 'sand'; className?: string }) {
  return (
    <section className={`py-16 md:py-24 ${className}`} style={{ backgroundColor: tone === 'sand' ? P.sand : '#FFFFFF' }}>
      <div className="mx-auto max-w-6xl px-5">{children}</div>
    </section>
  );
}

function H2({ children }: { children: ReactNode }) {
  return <h2 className="text-balance text-3xl font-extrabold leading-tight tracking-tight md:text-4xl">{children}</h2>;
}

// ----------------------------------------------------------------------------
// Seções
// ----------------------------------------------------------------------------

function Hero({
  page,
  broker,
  start,
  heroRef,
}: {
  page: AngelPage;
  broker: LandingProps['broker'];
  start: (cents: number) => void;
  heroRef: React.RefObject<HTMLElement>;
}) {
  const lead = page.lead;
  const name = lead?.name.replace(/^Residencial\s+/i, '');
  const cover = lead?.photos[0];
  return (
    <section ref={heroRef} className="relative md:min-h-[680px]" style={{ backgroundColor: P.sand }}>
      <div className="relative h-52 md:absolute md:inset-0 md:h-full">
        {cover ? (
          <img src={cover} alt={lead ? `Fachada do ${lead.name}` : ''} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full" style={{ backgroundColor: P.navy }} />
        )}
        {page.badge && (
          // Alinhado à coluna de conteúdo, não ao canto da tela.
          <div className="absolute inset-x-0 top-4 md:top-8">
            <div className="mx-auto max-w-6xl px-4 md:px-5">
              <span className="inline-block rounded-full bg-white px-3 py-1 text-sm font-bold shadow-sm" style={{ color: P.ink }}>
                {page.badge}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="relative mx-auto max-w-6xl md:flex md:justify-end md:px-5 md:py-16">
        <div className="relative -mt-8 rounded-t-3xl bg-white px-5 pb-8 pt-7 md:mt-0 md:max-w-xl md:rounded-3xl md:p-9 md:shadow-[0_24px_50px_-28px_rgba(24,34,74,.55)]">
          <h1 className="text-balance text-4xl font-extrabold leading-[1.05] tracking-tight md:text-5xl">
            {name ? `Conquiste seu apê no ${name}, em ${lead!.city}` : 'Conquiste seu apê pelo Minha Casa Minha Vida'}
          </h1>
          <p className="mt-3 text-lg leading-snug" style={{ color: P.muted }}>
            Análise gratuita pro Minha Casa Minha Vida, com atendimento direto comigo até a chave.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <img src={FACE} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
            <p className="text-sm leading-snug" style={{ color: P.muted }}>
              <span className="font-semibold" style={{ color: P.ink }}>
                Quem te atende: {broker.name}
              </span>
              <br />
              {CRECI} · grátis e sem compromisso
            </p>
          </div>
          <div className="mt-6">
            <IncomeQuestion start={start} id="hero-income" />
          </div>
          {page.detailsHref && (
            <Link to={page.detailsHref} className="mt-3 inline-block py-2 text-sm font-semibold underline underline-offset-4">
              Prefiro ver as fotos do apê primeiro
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

function FactsStrip({ page }: { page: AngelPage }) {
  const items = [...page.facts, page.income].filter(Boolean) as string[];
  if (!items.length) return null;
  return (
    <div className="border-b" style={{ borderColor: P.line }}>
      <ul className="mx-auto flex max-w-6xl flex-wrap gap-x-8 gap-y-3 px-5 py-6 text-base font-semibold">
        {items.map((f) => (
          <li key={f} className="flex items-center gap-2">
            <Check className="h-5 w-5 shrink-0" style={{ color: P.goldInk }} />
            {f}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Faixa 1 e 2 caem no botão "até 5 mil" do formulário.
const FAIXA_CENTS = [400_000, 400_000, 730_000, 1_130_000];

function Faixas({ page, start }: { page: AngelPage; start: (cents: number) => void }) {
  const floor = incomeFloor(page.income);
  const leadFaixa = floor ? FAIXAS.findIndex((f) => floor <= f.max) : -1;
  const leadName = page.lead?.name.replace(/^Residencial\s+/i, '');
  return (
    <Section>
      <div className="grid gap-10 md:grid-cols-[1fr_1.2fr] md:items-start">
        <div>
          <H2>Em qual faixa do MCMV a sua família está?</H2>
          <p className="mt-4 max-w-md text-base leading-relaxed" style={{ color: P.muted }}>
            A faixa sai da renda bruta da família, somando quem vai morar junto. Quanto menor a faixa, maior a ajuda do
            programa. Na análise eu confirmo a sua.
          </p>
          <p className="mt-3 text-sm" style={{ color: P.muted }}>
            Limites da portaria do Ministério das Cidades de março de 2026. Toque na sua faixa pra começar.
          </p>
        </div>
        <ol className="overflow-hidden rounded-2xl border" style={{ borderColor: P.line }}>
          {FAIXAS.map((f, i) => {
            const here = i === leadFaixa;
            return (
              <li key={f.name} className="border-t first:border-t-0" style={{ borderColor: P.line }}>
                <button
                  type="button"
                  onClick={() => start(FAIXA_CENTS[i] ?? 400_000)}
                  aria-label={`Começar a análise na ${f.name}, ${f.range}`}
                  className="group flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 px-5 py-4 text-left transition-colors hover:bg-[#FBF6EE]"
                  style={{ backgroundColor: here ? P.goldSoft : undefined }}
                >
                  <span className="text-lg font-bold">{f.name}</span>
                  <span className="flex items-center gap-2 text-base tabular-nums" style={{ color: P.muted }}>
                    {f.range}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                  {here && leadName && (
                    <span className="w-full text-sm font-semibold" style={{ color: P.goldInk }}>
                      {leadName}: {page.income?.replace(/^Renda/, 'renda')}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </Section>
  );
}

function Steps() {
  return (
    <Section tone="sand">
      <H2>Do aluguel à chave, em 4 passos</H2>
      <ol className="relative mt-10 grid gap-8 md:grid-cols-4 md:gap-6">
        <span aria-hidden="true" className="absolute left-6 top-6 hidden h-0.5 w-[calc(100%-3rem)] md:block" style={{ backgroundColor: P.line }} />
        {STEPS.map((s, i) => (
          <li key={s.title} className="relative grid grid-cols-[3rem_1fr] gap-4 md:block">
            <span
              className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full text-xl font-extrabold text-white"
              style={{ backgroundColor: P.navy }}
            >
              {i + 1}
            </span>
            <div className="md:mt-5">
              <h3 className="text-xl font-bold leading-tight">{s.title}</h3>
              {s.time && (
                <p className="mt-1 text-sm font-semibold" style={{ color: P.goldInk }}>
                  {s.time}
                </p>
              )}
              <p className="mt-2 text-base leading-relaxed" style={{ color: P.muted }}>
                {s.body}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
}

function Objections() {
  return (
    <Section>
      <div className="grid gap-10 md:grid-cols-[1fr_2fr]">
        <div>
          <H2>O que costuma travar, e como eu ajudo</H2>
          <p className="mt-4 text-base leading-relaxed" style={{ color: P.muted }}>
            São as quatro dúvidas que mais aparecem nas análises que eu faço.
          </p>
        </div>
        <dl className="grid gap-x-10 gap-y-8 sm:grid-cols-2">
          {OBJECTIONS.map((o) => (
            <div key={o.worry} className="border-t-2 pt-4" style={{ borderColor: P.navy }}>
              <dt className="text-xl font-bold">“{o.worry}”</dt>
              <dd className="mt-2 text-base leading-relaxed" style={{ color: P.muted }}>
                {o.answer}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </Section>
  );
}

function Building({ page, onCta }: { page: AngelPage; onCta: () => void }) {
  const lead = page.lead!;
  return (
    <Section tone="sand">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <H2>{lead.name}</H2>
          <p className="mt-2 flex items-start gap-1.5 text-base" style={{ color: P.muted }}>
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              {[lead.neighborhood, lead.city].filter(Boolean).join(', ')}
              {lead.tagline ? ` · ${lead.tagline}` : ''}
            </span>
          </p>
        </div>
        <Action page={page} onCta={onCta} label="Quero esse apê" />
      </div>
      {lead.photos.length > 0 && (
        <div className="mt-8 grid gap-3 md:grid-cols-3 md:grid-rows-2">
          {lead.photos.slice(0, 3).map((src, i) => (
            <Link
              key={src}
              to={page.detailsHref ?? '#'}
              aria-label={`Ver fotos e detalhes do ${lead.name}`}
              className={i === 0 ? 'md:col-span-2 md:row-span-2' : ''}
            >
              <img
                src={src}
                alt={`${lead.name}, foto ${i + 1} de ${lead.photos.length}`}
                loading="lazy"
                className={`w-full rounded-2xl ${i === 2 ? 'bg-white object-contain p-2' : 'object-cover'} ${i === 0 ? 'aspect-[4/3] md:aspect-auto md:h-full' : 'aspect-[4/3]'}`}
              />
            </Link>
          ))}
        </div>
      )}
      {page.nearby.length > 0 && (
        <div className="mt-10">
          <h3 className="text-xl font-bold">Perto de tudo</h3>
          <ul className="mt-4 grid gap-x-8 gap-y-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {page.nearby.map((n) => (
              <li key={n} className="flex items-start gap-2 text-base">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0" style={{ color: P.goldInk }} />
                {n}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-8 rounded-2xl px-5 py-4 text-base font-semibold" style={{ backgroundColor: P.goldSoft }}>
        {LAUNCH_NOTE}
      </p>
    </Section>
  );
}

function About({ broker, page }: { broker: LandingProps['broker']; page: AngelPage }) {
  const about = (broker.pageContent.aboutText ?? broker.bio ?? '').split('\n').filter(Boolean);
  const [lede, ...rest] = about;
  return (
    <Section>
      <div className="grid gap-10 md:grid-cols-[20rem_1fr] md:items-center">
        <img src={PORTRAIT} alt={`${broker.name}, corretora`} className="aspect-[4/5] w-full max-w-[20rem] rounded-3xl object-cover" />
        <div>
          <h2 className="text-4xl leading-tight md:text-5xl" style={{ fontFamily: SERIF }}>
            <span className="block text-2xl italic" style={{ color: P.goldInk }}>
              prazer, eu sou
            </span>
            <span className="italic">{broker.name}</span>
          </h2>
          <p className="mt-3 font-semibold">Corretora MCMV na Grande Florianópolis · {CRECI}</p>
          {lede && <p className="mt-6 max-w-[58ch] text-xl leading-relaxed">{lede}</p>}
          {rest.length > 0 && (
            <div className="mt-4 max-w-[62ch] space-y-3 text-base leading-relaxed" style={{ color: P.muted }}>
              {rest.map((p) => (
                <p key={p.slice(0, 24)}>{p}</p>
              ))}
            </div>
          )}
          {page.instagramHandle && (
            <a
              href={broker.pageContent.socialLinks?.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 py-2 font-semibold underline underline-offset-4"
            >
              <Instagram className="h-5 w-5" />
              Me acompanhe no Instagram: @{page.instagramHandle}
            </a>
          )}
        </div>
      </div>
    </Section>
  );
}

function Others({ page }: { page: AngelPage }) {
  const { filtered, filters } = usePropertyFilters(page.others);
  return (
    <Section tone="sand">
      <H2>Outros apês que eu atendo</H2>
      <PropertyFilterBar filters={filters} accent={P.navy} className="mt-6" />
      {filtered.length === 0 ? (
        <NoFilterMatch filters={filters} />
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => (
            <PropertyCard key={p.id} property={p} />
          ))}
        </div>
      )}
    </Section>
  );
}

function Faq({ items }: { items: Array<{ q: string; a: string }> }) {
  return (
    <Section>
      <div className="mx-auto max-w-3xl">
        <H2>Perguntas que todo mundo faz</H2>
        <div className="mt-6 divide-y" style={{ borderColor: P.line }}>
          {items.map((it) => (
            <details key={it.q} className="group" style={{ borderColor: P.line }}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-4 text-lg font-semibold [&::-webkit-details-marker]:hidden">
                {it.q}
                <Plus aria-hidden="true" className="h-5 w-5 shrink-0 transition-transform group-open:rotate-45" style={{ color: P.goldInk }} />
              </summary>
              <p className="max-w-[65ch] pb-5 text-base leading-relaxed" style={{ color: P.muted }}>
                {it.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </Section>
  );
}

function Final({ start }: { start: (cents: number) => void }) {
  return (
    <section data-dark style={{ backgroundColor: P.navy }}>
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-20 md:grid-cols-[1fr_1.1fr] md:items-center">
        <div className="text-white">
          <img src={FACE} alt="" className="mb-6 h-16 w-16 rounded-full object-cover ring-2 ring-white/40" />
          <h2 className="text-balance text-4xl font-extrabold leading-tight md:text-5xl">Seu primeiro apê começa com 2 minutos</h2>
          <p className="mt-4 max-w-md text-lg text-white/90">No fim, você já fala comigo no WhatsApp com as suas respostas.</p>
        </div>
        <IncomeQuestion start={start} tone="dark" id="final-income" />
      </div>
    </section>
  );
}

function Footer({ broker }: { broker: LandingProps['broker'] }) {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t" style={{ borderColor: P.line }}>
      <div className="mx-auto max-w-6xl space-y-2 px-5 pb-28 pt-10 text-sm md:pb-10" style={{ color: P.muted }}>
        <div className="text-2xl italic" style={{ fontFamily: SERIF, color: P.ink }}>
          {broker.name}
        </div>
        <p>Corretora de imóveis · {CRECI}</p>
        {broker.whatsappNumber && <p>WhatsApp {formatPhoneDisplay(broker.whatsappNumber)}</p>}
        <div className="flex flex-wrap items-center gap-x-4 pt-3">
          <span>© {year} {broker.name}</span>
          <a href="/privacidade" className="inline-block py-3 underline underline-offset-4">
            Política de privacidade
          </a>
        </div>
      </div>
    </footer>
  );
}
